<template>
  <div class="[&>.warning]:!m-0">
    <slot name="label" />
    <div class="flex relative">
      <input
        :id="id"
        v-model="inputValue"
        :data-cy="id"
        class="block w-full p-2.5 pr-8 text-sm bg-gray-50 border border-gray-300 rounded-sm focus:border-etBlue-primary hover:border-etBlue-primary focus:ring-0"
        :class="{
          'bg-white': !disabled,
          '!text-etRed-100 !border-etRed-100': isRequired
        }"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :disabled="disabled"
        :type="showPassword ? 'text' : 'password'"
        @click="copyMode && handleCopy()"
      />
      <template v-if="copyMode">
        <div
          class="cursor-pointer absolute right-2.5 bottom-2.5"
          @click="emit('copy', inputValue)"
        >
          <font-awesome-icon icon="fa-solid fa-clipboard" />
        </div>
      </template>
      <template v-else-if="!isRequired">
        <div class="cursor-pointer absolute right-2.5 bottom-2.5">
          <i
            :class="['fas', `${showPassword ? 'fa-eye-slash' : 'fa-eye'}`]"
            @click="toggleSecret"
          />
        </div>
      </template>
      <template v-if="isRequired">
        <div class="absolute right-2.5 bottom-2.5">
          <font-awesome-icon
            icon="fa-solid fa-triangle-exclamation"
            color="red"
          />
        </div>
      </template>
    </div>
    <div v-if="showStrengthMeter" class="relative mt-2 w-8/12">
      <div class="absolute bottom-0 w-full h-[4px] bg-etGray-primary rounded" />
      <small>{{ $t('shared.label.password_strength') }}</small>
      <password-meter
        :password="inputValue"
        class="relative w-0 bg-transparent z-50 !mt-1 !h-[4px]"
      />
      <slot name="warning" />
    </div>
  </div>
</template>
<script setup>
import i18n from '@/i18n'
import { onMounted, ref, watch } from 'vue'

const $t = i18n.t

const props = defineProps({
  value: String,
  disabled: Boolean,
  placeholder: String,
  autocomplete: String,
  copyMode: Boolean,
  id: String,
  showStrengthMeter: {
    type: Boolean,
    default: false
  }
})
defineExpose({ triggerValidaton, resetValidation })
const emit = defineEmits(['change', 'copy'])

const showPassword = ref(false)
const inputValue = ref('')
const isRequired = ref(false)
const isCopied = ref(false)

watch(
  () => inputValue.value,
  (value) => {
    emit('change', value)
    if (value === '') {
      isRequired.value = true
    } else {
      isRequired.value = false
    }
  }
)
watch(
  () => props.value,
  (value) => {
    inputValue.value = value
  }
)

onMounted(() => {
  inputValue.value = props.value
})

function handleCopy() {
  isCopied.value = true
  emit('copy', inputValue.value)
  setTimeout(() => {
    isCopied.value = false
  }, 5000)
}
function toggleSecret() {
  if (!props.copyMode) {
    showPassword.value = !showPassword.value
  }
}
function triggerValidaton() {
  isRequired.value = true
}
function resetValidation() {
  isRequired.value = false
}
</script>
<style lang="scss">
.po-password-strength-bar {
  border-radius: 2px;
  transition: all 0.2s linear;
  height: 5px;
  margin-top: 8px;
}
.po-password-strength-bar.risky {
  background-color: #e02424;
  width: 10%;
}
.po-password-strength-bar.guessable {
  background-color: #c27803;
  width: 32.5%;
}
.po-password-strength-bar.weak {
  background-color: #faca15;
  width: 55%;
}
.po-password-strength-bar.safe {
  background-color: #84e1bc;
  width: 77.5%;
}
.po-password-strength-bar.secure {
  background-color: #31c48d;
  width: 100%;
}
</style>
